interface MonetaryAmountDistribution {
  '@type': string
  'name': string
  'currency': string
  'duration': string
  'minValue'?: number
  'maxValue'?: number
  'median'?: number
  'percentile10'?: number
  'percentile25'?: number
  'percentile75'?: number
  'percentile90'?: number
  'description'?: string
}
interface PlaceLocation {
  '@type': string
  'name': string
}
interface WebPage {
  '@type': string
  '@id': string
  'lastReviewed'?: string
}
interface HiringOrganization {
  '@type': string
  'name': string
  'url'?: string
}
interface EstimatedSalarySchema {
  '@context': string
  '@type': string
  'name': string
  'mainEntityOfPage'?: WebPage
  'industry'?: string
  'description': string
  'estimatedSalary': MonetaryAmountDistribution[]
  'occupationLocation'?: PlaceLocation[]
  'occupationalCategory'?: string
  'hiringOrganization'?: HiringOrganization
  'skills'?: string[]
  'url'?: string
  'sampleSize'?: number
  'yearsExperienceMin'?: number
  'yearsExperienceMax'?: number
  'sourceOrganization': {
    '@type': string
    'name': string
    'url': string
  }
}
interface EstimatedSalaryOptions {
  occupationName: string
  description: string
  minSalary?: number
  maxSalary?: number
  medianSalary?: number
  percentile10?: number
  percentile25?: number
  percentile75?: number
  percentile90?: number
  currency?: string
  paymentPeriod?: string
  city?: string
  state?: string
  country?: string
  occupationalCategory?: string
  skills?: string[]
  url?: string
  sampleSize?: number
  yearsExperienceMin?: number
  yearsExperienceMax?: number
  lastReviewed?: Date | string
  industry?: string
  companyName?: string
  companyPermalink?: string
  roleDescription?: string | null
}
export function generateEstimatedSalarySchema({
  occupationName,
  description,
  minSalary,
  maxSalary,
  medianSalary,
  percentile10,
  percentile25,
  percentile75,
  percentile90,
  currency = 'USD',
  paymentPeriod = 'P1Y',
  city,
  state,
  country = 'US',
  occupationalCategory,
  industry,
  skills,
  url,
  sampleSize,
  yearsExperienceMin,
  yearsExperienceMax,
  lastReviewed,
  companyName,
  companyPermalink,
  roleDescription
}: EstimatedSalaryOptions): EstimatedSalarySchema {
  const schema: EstimatedSalarySchema = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    'name': occupationName,
    'description': description,
    'estimatedSalary': [
      {
        '@type': 'MonetaryAmountDistribution',
        'name': 'base',
        'currency': currency,
        'duration': paymentPeriod,
        'description':
          'Salary distribution estimated from job listings and BLS data.'
      }
    ],
    'sourceOrganization': {
      '@type': 'Organization',
      'name': 'employers.io and U.S. Bureau of Labor Statistics',
      'url': 'https://employers.io'
    }
  }
  const baseDistribution = schema.estimatedSalary[0]
  if (minSalary !== undefined) baseDistribution.minValue = minSalary
  if (maxSalary !== undefined) baseDistribution.maxValue = maxSalary
  if (medianSalary !== undefined) baseDistribution.median = medianSalary
  if (percentile10 !== undefined) baseDistribution.percentile10 = percentile10
  if (percentile25 !== undefined) baseDistribution.percentile25 = percentile25
  if (percentile75 !== undefined) baseDistribution.percentile75 = percentile75
  if (percentile90 !== undefined) baseDistribution.percentile90 = percentile90
  if (city || state || country) {
    schema.occupationLocation = []
    if (city) {
      schema.occupationLocation.push({
        '@type': 'City',
        'name': city
      })
    }
    if (state) {
      schema.occupationLocation.push({
        '@type': 'State',
        'name': state
      })
    }
    if (country) {
      schema.occupationLocation.push({
        '@type': 'Country',
        'name': country
      })
    }
  }
  if (url || lastReviewed) {
    schema.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': url || window.location.href
    }
    if (lastReviewed) {
      const formattedDate
        = typeof lastReviewed === 'string'
          ? lastReviewed
          : lastReviewed.toISOString().split('T')[0]
      schema.mainEntityOfPage.lastReviewed = formattedDate
    }
  }
  if (industry) {
    schema.industry = industry
  }
  if (companyName) {
    schema.hiringOrganization = {
      '@type': 'Organization',
      'name': companyName
    }
    if (companyPermalink) {
      schema.hiringOrganization.url = `https://employers.io/company/${companyPermalink}`
    }
  }
  if (occupationalCategory) {
    schema.occupationalCategory = occupationalCategory
  }
  if (skills && skills.length > 0) {
    schema.skills = skills
  }
  if (url) {
    schema.url = url
  }
  if (sampleSize !== undefined) {
    schema.sampleSize = sampleSize
  }
  if (yearsExperienceMin !== undefined) {
    schema.yearsExperienceMin = yearsExperienceMin
  }
  if (yearsExperienceMax !== undefined) {
    schema.yearsExperienceMax = yearsExperienceMax
  }
  return schema
}
